<template>
    <!-- The default <HeadMeta> in InitWrapper is hydrated lazily, so without LazyHydration here, the default will mount later and override meta -->
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMetaGame
            :game="game"
            :gameCatInfo="gameCatInfo"
        />
    </LazyHydration>
    <div
        v-if="game"
        :class="$style.two_columns_wrapper"
    >
        <ExportInitScreen
            v-if="showInitScreen"
            :class="$style.init_screen_override"
            :game="game"
            :categories="gameCatInfo"
            :isFullscreen="isFullscreen"
            @click="onInitScreenClick"
        />
        <GameFrameArea
            v-else
            :game="game"
            :categories="gameCatInfo"
            :gameFrame="gameFrame"
            :isFullscreen="isFullscreen"
            :isTabsOpened="isTabsOpened"
            :showMobileInterface="showMobileInterface"
            :showGameHeader="showGameHeader"
            :shouldShowLoginPrompt="shouldShowLoginPrompt"
            :hideBranding="isExportOnWhitelabel"
            :widgetMode="isExportOnWidget"
            @closeGame="closeGame"
            @loginPromptClose="closeLoginPrompt"
            @loginPromptLogin="handleLogin"
        />
        <OutOfPageAdContainer
            ref="out-of-page-ad-container"
            :adInfo="gameFrame.adInfo.value"
            :isAdFreeButtonShown="false"
            :isAdFreeActive="adDisabledStore.isAdFreeActive"
        />
    </div>
    <ErrorContent
        v-else-if="pageStatus !== 200"
        noRandomGame
        :type="pageStatus"
    />
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import {
    computed,
    hydrateOnIdle,
    onBeforeUnmount,
    onMounted,
    onServerPrefetch,
    ref,
    shallowRef,
    toRef,
    useTemplateRef,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ErrorContent from '@/components_new/ErrorContent/ErrorContent.vue'
import LazyHydration from '@/components_new/LazyHydration.vue'
import { useClidHelpers } from '@/composables/use-clid-helpers'
import { OPPO_WL_CLID } from '@/constants/general'
import type { ChargeUserFunction } from '@/modules/in-game-payments'
import { useNativeExactPackPurchase } from '@/modules/native-payments/native-exact-pack-purchase'
import { ROUTE_NAMES } from '@/router/constants'
import { CLID_PARAM, EXPORT_MODE_PARAM } from '@/router/query-params'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import type { GameAttributes, User } from '@/types'
import safeURLParse from '@/utils/safeURLParse'

import ExportInitScreen from './components/ExportInitScreen/ExportInitScreen.vue'
import GameFrameArea from './components/GameFrameArea.vue'
import HeadMetaGame from './components/HeadMetaGame.vue'
import OutOfPageAdContainer from './components/OutOfPageAdContainer/OutOfPageAdContainer.vue'
import { useGameFrame } from './composables/use-game-frame'
import { useGameIframeUrl } from './composables/use-game-iframe-url'
import { useGamePingSession } from './composables/use-game-ping-session'
import { useStartGameSound } from './composables/use-start-game-sound'
import { useGamePageData } from './use-game-page-data'

const PARTNER_EXPORT_GAME_REDIRECTS: Record<string, { redirectUrl: string; status: 301 | 302 }> = {
    'mr-racer-car-racing': {
        redirectUrl: 'https://mrracer.online/export',
        status: 301,
    },
}

const route = useRoute()
const router = useRouter()
const hru = toRef(() => route.params.game as string)

const appStore = useAppStore()
const { pageStatus, isExportOnWidget, isExportOnWhitelabel } = storeToRefs(appStore)
const { game, gameCatInfo, preloadGameData, processGameResponse } = useGamePageData({ hru, isExport: true })

const { clidConfig } = useClidHelpers()
const adsStore = useAdsStore()
const loggerStore = useLoggerStore()
const userStore = useUserStore()
const { chargeUser: chargeUserWithNativeExactPack } = useNativeExactPackPurchase(userStore.chargeUser)
const adDisabledStore = useAdDisabledStore()

const gameFrame = useGameFrame()
const { buildGameIframeUrl } = useGameIframeUrl()
const { loadStartGameSound, playStartGameSound } = useStartGameSound()

const outOfPageAdContainerRef = useTemplateRef('out-of-page-ad-container')

const isFullscreen = ref(true)

const showInitScreen = computed(() => isInitGameScreenDisplayed.value && !isExportOnWidget.value)
const isTabsOpened = ref(false)
const gameOpeningType = ref('')
const isInitGameScreenDisplayed = ref(!appStore.isNativeAppEnv && !clidConfig.value.isInitGameScreenHidden)

const { startPingSession, stopPingSession, shouldShowLoginPrompt, closeLoginPrompt, handleLogin } = useGamePingSession({
    game,
    isFullscreen,
    lastAdResult: gameFrame.lastAdResult,
    loginPromptEnabled: false,
})

const showMobileInterface = computed(
    () =>
        appStore.platform !== 'desktop' &&
        !!game.value &&
        !showInitScreen.value &&
        (isExportOnWidget.value || isExportOnWhitelabel.value),
)
const showGameHeader = computed(() => showMobileInterface.value && isExportOnWhitelabel.value)
const isOppoWhitelabelExport = computed(
    () => route.query[CLID_PARAM] === OPPO_WL_CLID || appStore.clid === OPPO_WL_CLID,
)

const handleCloseGame = shallowRef<() => void>()

function getPartnerExportGameRedirect() {
    const config = PARTNER_EXPORT_GAME_REDIRECTS[hru.value]

    if (!config || route.query[EXPORT_MODE_PARAM] !== undefined) {
        return
    }

    const target = safeURLParse(config.redirectUrl)
    if (!target) {
        return
    }

    target.searchParams.set(CLID_PARAM, typeof route.query[CLID_PARAM] === 'string' ? route.query[CLID_PARAM] : '')

    return {
        status: config.status,
        target: target.toString(),
    }
}

function closeGame() {
    handleCloseGame.value?.()
}

function closeStandaloneWhitelabelGame() {
    router.push({ name: ROUTE_NAMES.WHITE_LABEL_2PAGE })
}

function getParentExportApi() {
    try {
        return window.parent.exportApi
    } catch {
        return undefined
    }
}

function handleParentWindowMessage({ data }: MessageEvent) {
    const isExportOnPlaygama = isExportOnWidget.value || isExportOnWhitelabel.value

    if (data.type === 'setOutOfPageAdPlaybackState' && isExportOnPlaygama) {
        adsStore.adService?.setOutOfPageAdPlaybackState(data.play)
    }

    if (data.type === 'userChanged' && isExportOnPlaygama) {
        userStore.setUser(JSON.parse(data.user) as User | null)
    }

    if (data.type === 'toggleFullscreen' && isExportOnPlaygama) {
        isFullscreen.value = data.isFullscreen
    }

    if (data.type === 'setTabsOpened' && isExportOnPlaygama) {
        isTabsOpened.value = data.isTabsOpened
    }
}

onMounted(async () => {
    window.addEventListener('message', handleParentWindowMessage)

    if (pageStatus.value !== 200) {
        return
    }
    await preloadGameData()

    // TODO: make this more transparent
    // but should never happen, otherwise `new URL(game.value.game_url)` would cause errors
    if (!game.value) {
        console.error('Error: No game found')
        return
    }

    if (!adsStore.adService) {
        console.error('Error: No ad service was passed to the main frame')
        return
    }

    adsStore.updateTargeting({ content_category: game.value.main_category, provider: game.value.source || '' })

    const url = buildGameIframeUrl(game.value)

    if (!url) {
        return
    }

    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'game_opened',
    })

    let { authorizeUser, getUser, cancelCharge } = userStore
    let chargeUser: ChargeUserFunction = chargeUserWithNativeExactPack
    let attributes: GameAttributes = {
        clid: appStore.clid,
        widgetId: appStore.widgetId,
        userAcquisitionParams: appStore.userAcquisitionParams,
    }

    if (isExportOnWidget.value || isExportOnWhitelabel.value) {
        const parentExportApi = getParentExportApi()
        if (parentExportApi) {
            authorizeUser = () =>
                parentExportApi.authorizeUser(isExportOnWidget.value ? 'export-page-widget' : 'export-page-portal')
            getUser = parentExportApi.getUser
            chargeUser = parentExportApi.chargeUser
            cancelCharge = parentExportApi.cancelCharge
            handleCloseGame.value = parentExportApi.closeGame
            attributes = parentExportApi.getGameAttributes()
            gameOpeningType.value = parentExportApi.getGameOpeningType?.() ?? ''
        } else {
            authorizeUser = () => authorizeUser('export-page')
        }
    }

    if (isExportOnWhitelabel.value && !handleCloseGame.value) {
        handleCloseGame.value = isOppoWhitelabelExport.value
            ? closeStandaloneWhitelabelGame
            : () => {
                  window.parent?.postMessage({ playgama_export: { type: 'close' } }, '*')
              }
    }

    gameFrame.initGameFrame({
        game: game.value,
        gameUrl: url,
        adConfig: clidConfig.value,
        adService: adsStore.adService,
        userApi: {
            authorizeUser: authorizeUser as () => Promise<User | null>,
            getUser,
            chargeUser,
            cancelCharge,
        },
        attributes,
        adBlockerOfferEnabled: false,
        affiliateLoader: true,
        getAdRootElementId: () => outOfPageAdContainerRef.value?.containerId,
    })

    if (!showInitScreen.value) {
        startGame()
    } else {
        loadStartGameSound()
    }
})

function startGame() {
    isInitGameScreenDisplayed.value = false

    startPingSession()

    if (isExportOnWidget.value || clidConfig.value.isPrerollDisabled || adDisabledStore.isAdDisabled) {
        gameFrame.startGameLoading()
    } else {
        gameFrame.showPreroll()
    }
}

function onInitScreenClick() {
    playStartGameSound()

    isInitGameScreenDisplayed.value = false

    startGame()

    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'export-init-click',
        dimension1: gameOpeningType.value,
    })
}

onBeforeUnmount(() => {
    window.removeEventListener('message', handleParentWindowMessage)
    gameFrame.destroy()
    stopPingSession()
})

onServerPrefetch(async () => {
    const redirect = getPartnerExportGameRedirect()
    if (redirect) {
        appStore.setPageStatus(redirect.status)
        appStore.setTargetURL(redirect.target)
        return
    }

    await preloadGameData()
    processGameResponse()
})
</script>
<style module>
.two_columns_wrapper {
    height: 100svh;
    display: grid;
}

.init_screen_override {
    height: 100%;
}

.sidebar_override {
    display: none;
}

@media (--right-column-fits) {
    .two_columns_wrapper:has(.sidebar_enabled_override) {
        grid-template-columns: 1fr auto;
        gap: 8px;
    }

    .sidebar_enabled_override {
        display: block;
        overflow: hidden;
    }
}
</style>
